import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  IconButton,
  Typography,
  Paper,
  CircularProgress,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Card,
  CardMedia,
  CardContent,
  CardActionArea,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  MoreVert as MoreVertIcon,
  ViewList as ViewListIcon,
  GridView as GridViewIcon,
  FolderOpen as FolderOpenIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useFiles } from '../hooks/useFilesContext.js';
import { useDropzone } from 'react-dropzone';
import ContextMenu from './ContextMenu.js';
import ImagePreviewModal from './ImagePreviewModal.js';
import CreateFolderModal from './CreateFolderModal.js';

const XANO_BASE_URL = 'https://xlxn-yfz8-5hn7.n7d.xano.io';

function FileList() {
  const {
    files,
    folders,
    loading,
    loadingMore,
    error,
    currentFolder,
    navigateToFolder,
    deleteFile,
    deleteFolder,
    handleUpload,
    loadMoreFiles,
    hasMore
  } = useFiles();

  const [contextMenu, setContextMenu] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [viewMode, setViewMode] = useState('list');
  const [previewFile, setPreviewFile] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [createFolderModalOpen, setCreateFolderModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const observer = useRef();
  const lastFileElementRef = useCallback(node => {
    if (loadingMore) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        loadMoreFiles();
      }
    });
    if (node) observer.current.observe(node);
  }, [loadingMore, hasMore, loadMoreFiles]);

  const onDrop = useCallback((acceptedFiles) => {
    handleUpload(acceptedFiles);
  }, [handleUpload]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const handleContextMenu = (event, item, type) => {
    event.preventDefault();
    setContextMenu({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
    });
    setSelectedItem({ ...item, type });
  };

  const handleClose = () => {
    setContextMenu(null);
    setSelectedItem(null);
  };

  const handleDelete = async () => {
    if (selectedItem) {
      setDeleteConfirmOpen(true);
      setContextMenu(null);
    }
  };

  const confirmDelete = async () => {
    if (selectedItem?.type) {
      setIsDeleting(true);
      try {
        if (selectedItem.type === 'folder') {
          await deleteFolder(selectedItem.id);
        } else {
          await deleteFile(selectedItem.id);
        }
        setDeleteConfirmOpen(false);
        setSelectedItem(null);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleFolderClick = (folderId) => {
    navigateToFolder(folderId);
  };

  const handleViewModeChange = (event, newViewMode) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  const isImageFile = (file) => {
    return file.type.startsWith('image');
  };

  const handleFileClick = (file) => {
    if (isImageFile(file)) {
      setPreviewFile(file);
    }
  };

  const handleClosePreview = () => {
    setPreviewFile(null);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  // Filtra as pastas principais (sem parent_folder_id e não é a pasta principal)
  const mainFolders = folders.filter(folder => !folder.parent_folder_id && !folder.is_trashed && !folder.is_main);
  
  // Filtra as subpastas da pasta atual
  const subFolders = folders.filter(folder => folder.parent_folder_id === currentFolder && !folder.is_trashed && !folder.is_main);
  
  // Encontra o ID da pasta raiz (is_main = true)
  const rootFolderId = folders.find(folder => folder.is_main)?.id;

  // Verifica se estamos na pasta raiz
  const isRootFolder = currentFolder === rootFolderId;

  // Filtra os arquivos da pasta atual
  const currentFiles = Array.isArray(files) ? files.filter(file => {
    if (file.is_trashed) return false;
    if (isRootFolder) {
      // Na raiz, mostra arquivos da pasta principal (is_main)
      return file.folder_id === rootFolderId;
    }
    // Em uma pasta específica, mostra arquivos daquela pasta
    return file.folder_id === currentFolder;
  }) : [];

  const isEmpty = subFolders.length === 0 && currentFiles.length === 0;

  const renderBackButton = () => {
    if (isRootFolder) return null;
    
    const parentFolder = folders.find(folder => folder.id === currentFolder);
    if (!parentFolder) return null;

    return (
      <Box sx={{ mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigateToFolder(parentFolder.parent_folder_id)}
          sx={{ textTransform: 'none' }}
        >
          Voltar
        </Button>
      </Box>
    );
  };

  const renderEmptyState = () => (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="200px"
      gap={2}
    >
      <FolderOpenIcon sx={{ fontSize: 60, color: 'text.secondary' }} />
      <Typography variant="h6" color="text.secondary">
        Esta pasta está vazia
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Arraste arquivos para esta pasta ou use o botão de upload
      </Typography>
    </Box>
  );

  const renderListView = () => (
    <List>
      {isRootFolder && mainFolders.map((folder) => (
        <ListItem
          key={folder.id}
          disablePadding
          secondaryAction={
            <IconButton edge="end" onClick={(e) => handleContextMenu(e, folder, 'folder')}>
              <MoreVertIcon />
            </IconButton>
          }
          onContextMenu={(e) => handleContextMenu(e, folder, 'folder')}
        >
          <ListItemButton onClick={() => handleFolderClick(folder.id)}>
            <ListItemIcon>
              <FolderIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary={folder.name} />
          </ListItemButton>
        </ListItem>
      ))}

      {subFolders.map((folder) => (
        <ListItem
          key={folder.id}
          disablePadding
          secondaryAction={
            <IconButton edge="end" onClick={(e) => handleContextMenu(e, folder, 'folder')}>
              <MoreVertIcon />
            </IconButton>
          }
          onContextMenu={(e) => handleContextMenu(e, folder, 'folder')}
        >
          <ListItemButton onClick={() => handleFolderClick(folder.id)}>
            <ListItemIcon>
              <FolderIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary={folder.name} />
          </ListItemButton>
        </ListItem>
      ))}

      {currentFiles.map((file, index) => (
        <ListItem
          key={file.id}
          ref={index === currentFiles.length - 1 ? lastFileElementRef : null}
          disablePadding
          secondaryAction={
            <IconButton edge="end" onClick={(e) => handleContextMenu(e, file, 'file')}>
              <MoreVertIcon />
            </IconButton>
          }
          onContextMenu={(e) => handleContextMenu(e, file, 'file')}
        >
          <ListItemButton onClick={() => handleFileClick(file)}>
            <ListItemIcon>
              {isImageFile(file) ? (
                <Box
                  component="img"
                  src={`${XANO_BASE_URL}${file.path}?tpl=small`}
                  alt={file.name}
                  sx={{ width: 40, height: 40, objectFit: 'cover' }}
                />
              ) : (
                <FileIcon />
              )}
            </ListItemIcon>
            <ListItemText
              primary={file.name}
              secondary={`${(file.size / 1024 / 1024).toFixed(2)} MB`}
            />
          </ListItemButton>
        </ListItem>
      ))}

      {loadingMore && (
        <Box display="flex" justifyContent="center" p={2}>
          <CircularProgress size={24} />
        </Box>
      )}
    </List>
  );

  const renderGridView = () => (
    <Grid container spacing={2} sx={{ p: 2 }}>
      {isRootFolder && mainFolders.map((folder) => (
        <Grid item xs={6} sm={4} md={3} key={folder.id}>
          <Card>
            <CardActionArea onClick={() => handleFolderClick(folder.id)}>
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                <FolderIcon color="primary" sx={{ fontSize: 60 }} />
              </Box>
              <CardContent>
                <Typography noWrap align="center">
                  {folder.name}
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
      ))}

      {subFolders.map((folder) => (
        <Grid item xs={6} sm={4} md={3} key={folder.id}>
          <Card>
            <CardActionArea onClick={() => handleFolderClick(folder.id)}>
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                <FolderIcon color="primary" sx={{ fontSize: 60 }} />
              </Box>
              <CardContent>
                <Typography noWrap align="center">
                  {folder.name}
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
      ))}

      {currentFiles.map((file, index) => (
        <Grid item xs={6} sm={4} md={3} key={file.id} ref={index === currentFiles.length - 1 ? lastFileElementRef : null}>
          <Card>
            <CardActionArea onClick={() => handleFileClick(file)}>
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                {isImageFile(file) ? (
                  <CardMedia
                    component="img"
                    image={`${XANO_BASE_URL}${file.path}?tpl=medium`}
                    alt={file.name}
                    sx={{ height: 140, objectFit: 'cover' }}
                  />
                ) : (
                  <FileIcon sx={{ fontSize: 60 }} />
                )}
              </Box>
              <CardContent>
                <Typography noWrap align="center">
                  {file.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center">
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
      ))}

      {loadingMore && (
        <Grid item xs={12} display="flex" justifyContent="center" p={2}>
          <CircularProgress size={24} />
        </Grid>
      )}
    </Grid>
  );

  return (
    <Paper elevation={0} sx={{ backgroundColor: 'transparent' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            size="small"
          >
            <ToggleButton value="list">
              <ViewListIcon />
            </ToggleButton>
            <ToggleButton value="grid">
              <GridViewIcon />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      <Paper 
        sx={{ 
          p: 2, 
          backgroundColor: '#F9F9F9',
          ...(isDragActive && {
            backgroundColor: '#E3F2FD',
            border: '2px dashed #2196F3'
          })
        }}
      >
        <Box
          {...getRootProps()}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1000,
            pointerEvents: isDragActive ? 'auto' : 'none',
            backgroundColor: isDragActive ? 'rgba(33, 150, 243, 0.1)' : 'transparent',
            border: isDragActive ? '2px dashed #2196F3' : 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <input {...getInputProps()} />
          {isDragActive && (
            <Typography variant="h6" color="primary">
              Solte os arquivos aqui...
            </Typography>
          )}
        </Box>
        {renderBackButton()}
        {isEmpty ? renderEmptyState() : (
          viewMode === 'list' ? renderListView() : renderGridView()
        )}
      </Paper>

      <ContextMenu
        contextMenu={contextMenu}
        handleClose={handleClose}
        handleDelete={handleDelete}
        selectedItem={selectedItem}
      />

      <ImagePreviewModal
        open={!!previewFile}
        onClose={handleClosePreview}
        file={previewFile}
      />

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            {selectedItem?.type === 'folder' 
              ? 'Tem certeza que deseja excluir esta pasta? Todos os arquivos dentro dela também serão excluídos.'
              : 'Tem certeza que deseja excluir este arquivo?'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteConfirmOpen(false)}
            disabled={isDeleting}
          >
            Cancelar
          </Button>
          <Button 
            onClick={confirmDelete} 
            color="error"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isDeleting ? 'Excluindo...' : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>

      <CreateFolderModal
        open={createFolderModalOpen}
        onClose={() => setCreateFolderModalOpen(false)}
      />
    </Paper>
  );
}

export default FileList; 